import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Card, Col, Row } from 'antd';
import configurationStore from 'common/stores/configuration/configuration.store';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import TableBase from 'common/components/table/table-base';
import {
  cardClassesTableInitialColumns,
  facilityCodesTableInitialColumns,
  panelsTableInitialColumns,
} from '../community-information.config';
import '../community-information.scss';
import { trackPromise } from 'react-promise-tracker';
import residentsService from 'common/services/residents-service/residents.service';
import dayjs, { Dayjs } from 'dayjs';
import { ISystemConfigurationRead } from 'common/services/configuration-service/configuration.service.types';
import configurationService from 'common/services/configuration-service/configuration.service';
import cardAccessService from 'common/services/card-access-service/card-access.service';
import { ICardAccessPanel } from 'common/services/card-access-service/card-access.service.types';

export const CardAccessConfiguration = observer(() => {
  const [isPushDisabled, setBushDisabled] = useState(false);
  const [date, setDate] = useState<Dayjs | null>();
  const [panels, setPanels] = useState<ICardAccessPanel[]>([]);

  const setButtonStatus = (configuration: ISystemConfigurationRead | null) => {
    const lastPushAt = configuration?.cardAccessConfiguration?.lastPushAt;
    const maxDate = lastPushAt ? dayjs.utc(lastPushAt) : null;

    setDate(maxDate);

    if (maxDate?.local().add(1, 'hour').isAfter(dayjs())) {
      setBushDisabled(true);
    } else {
      setBushDisabled(false);
    }
  };

  useEffect(() => {
    setButtonStatus(configurationStore.configuration);
    getPanelsData();
  }, []);

  const cardClassesData = useMemo(() => {
    return configurationStore.communitySettings?.cardAccessGroupsSettings?.map((i) => ({ name: i, id: i }));
  }, [configurationStore.communitySettings]);

  const facilityCodesData = useMemo(() => {
    return configurationStore.configuration?.facilityCodes.map((i) => ({ ...i, id: i.value }));
  }, [configurationStore.configuration]);

  const cardClassesTableControls = useTableControls({
    data: cardClassesData,
    initialColumns: cardClassesTableInitialColumns,
    clientSide: true,
  });

  const facilityCodesTableControls = useTableControls({
    data: facilityCodesData,
    initialColumns: facilityCodesTableInitialColumns,
    clientSide: true,
  });

  const panelsTableContext = useTableControls({
    data: panels,
    clientSide: true,
    initialColumns: panelsTableInitialColumns,
  });

  const onPushDevicesButtonClicked = async () => {
    setBushDisabled(true);

    await trackPromise(residentsService.runCardAccessSync());

    var configuration = await configurationService.getSystemConfiguration();
    setButtonStatus(configuration);
    configurationStore.setConfiguration(configuration);
  };

  const getPanelsData = async () => {
    const data = await trackPromise(cardAccessService.getPanels());
    setPanels(data.map((item, index) => ({ ...item, id: index })));
  };

  return (
    <Card className="eiq-card eiq-card-border eiq-configuration welcome-emails">
      <Row className="eiq-card-header">
        <Col span={24}>
          <h1 className="h1">Card Access</h1>
        </Col>
      </Row>
      <Row>
        <div className="community-information__row-info">
          Server Name: {configurationStore.configuration?.cardAccessConfiguration?.serverName}
        </div>
      </Row>
      <Row>
        <div className="community-information__row-info">
          Group Number: {configurationStore.configuration?.cardAccessConfiguration?.groupNumber}
        </div>
      </Row>
      <Row>
        <div>
          <div className="send-btn-wrapper">
            <Button type="primary" onClick={onPushDevicesButtonClicked} disabled={isPushDisabled}>
              Push All Devices to Card Access
            </Button>
            {isPushDisabled && date && <span>Will be available at {date.local().add(1, 'hour').format('MM/DD/YYYY hh:mm A')}</span>}
          </div>
          <div>Press only once. Pushes all devices, credentials to card access software.</div>
        </div>
      </Row>
      <Row>
        <div>
          <h3>Panels</h3>
          <div className="send-btn-wrapper get-panel-statues">
            <Button type="primary" onClick={getPanelsData}>
              Get Panel Statuses
            </Button>
          </div>
        </div>
      </Row>
      {!!panels.length && (
        <TableContextProvider controls={panelsTableContext}>
          <TableBase selectFirstRowByDefault={false} />
        </TableContextProvider>
      )}
      <TableContextProvider controls={facilityCodesTableControls}>
        <h3>Facilities</h3>
        <TableBase />
      </TableContextProvider>
      <TableContextProvider controls={cardClassesTableControls}>
        <h3>Card Classes</h3>
        <TableBase />
      </TableContextProvider>
    </Card>
  );
});
